/**
 * Definizione dell' interfaccia e della classe AziendaRecord, la struttura dati che rappresenta l'azienda (sia società sia sede/unità locale)
 * La società è rappresentata quando idAzienda===idAziendaMaster, altrimenti viene rappresentata una sede/unità locale
 */

import {AziendaBase} from "./AziendaNavRecord";
import {PVABase} from "@commons/mapping/PVABase";
import {ComuneRecord} from "@commons/mapping/ComuneRecord";
import {CCNLRecord} from "@commons/mapping/SettoriCCNL";
import {PeriodoGiustificato, Trace} from "@commons/types";
import {DateTime} from "luxon";

interface CodificaAzienda {
    codAzienda: string;
    codUL: string;
}

export interface IndirizzoAzienda {
    provinciaSigla: string,
    provinciaDescrizione?: any,
    comuneDescrizione: string | null,
    comuneCodCatasto: string | null,
    comune?: ComuneRecord | null, // solo per frontend, da NON inviare al backend
    cap: number | null,
    multiCap: boolean,
    indirizzoVia: string,
    indirizzoVia2: string,
    indirizzoNumero: string,
    telefono: string,
    telefonoMobile?: string,
    fax: string,
    email: string,
}

interface ContattoAzienda {
    telefono: string;
    fax: string;
    email: string;
}

interface EstremiSantoPatrono {
    applicaConvenzione: boolean | string | null;
    meseConvenzione: number;
    giornoConvenzione: number;
    codGiustificativo: string;
    mese: number;
    giornoFisso: number;
    variabile: boolean;
    giornata: number;
    tipoRiferimento: string;
    giornoRiferimento: number;
    giornoVariabile: number;
}

interface CanoneServizio {
    cadenzaFatturazione: string;
    costoUnitario: number;
    costoTotale: number;
}

interface ConfigCollocamento {
    attivaTabModelloQ: boolean;
    attivaTabVisiteMediche: boolean;
    attivaTabPVA: boolean;
    idPianoCosti: number;
    attivaTabAgevolazioneST: boolean;
    inviaNotifiche: boolean;
}

export interface AziendaRecord extends AziendaBase {
    URI: string | null; // Ridefinisco URI, non più opzionale

    descrizione: string;
    idAziendaOrari: number;
    idAziendaGAP: number;
    idAziendaBanche: number;
    idAziendaTLR: number;
    personaFisica: string;
    codiceFiscale: string;
    partitaIva: string;
    paghe: CodificaAzienda;
    hr: CodificaAzienda;
    altraCodifica: CodificaAzienda;
    ticketR: CodificaAzienda;
    codFatturazione: string;
    puntoConsegnaTicketR: string;
    valoreUnitarioTicketR: number;
    domAmm: IndirizzoAzienda;
    sedeLegale: IndirizzoAzienda;
    contatto: ContattoAzienda;
    idCED: number;
    idCDL: number;
    idRDT_L196: number;
    idCommercialista: number;
    santoPatrono: EstremiSantoPatrono;
    codGiustificativoFS: string;
    codGiustificativoFC: string;
    codGiustificativoFS_4_11: string;
    meseFC: number;
    giornoFC: number;
    inizioFY: number;
    generaVariazioniRML: boolean;
    spaccaturaElab: string;
    codAteco: string;
    codEntePrevidenziale: string;
    idGestioneInail: number;
    idSedeInail: number;
    codDittaInail: string;
    ambitoPresenze: boolean;
    ambitoCollocamento: boolean;
    ambitoHR: boolean;
    ambitoPVA: boolean;
    ambitoGAP: boolean;
    ambitoDOCS: boolean;
    idOwnerEmployee: number;
    idProfiloAccesso: number;
    idProfiloPresenze: number;
    idTabellaPaghe: number;
    meseDifferito: boolean;
    opzioniScorporo: number;
    canone: CanoneServizio;

    settoreCCNL: string;
    codContrattoUNILAV: string | null;
    codContrattoCCNL: string;
    CCNL?: CCNLRecord | null,// solo per frontend, da NON inviare al backend, valorizza codContrattoUNILAV e codContrattoCCNL

    codContrattoUNILAVDirigenti: string;
    codContrattoCCNLDirigenti: string;
    CCNLDirigenti?: CCNLRecord | null,// solo per frontend, da NON inviare al backend, valorizza codContrattoUNILAVDirigenti e codContrattoCCNLDirigenti

    pva: PVABase;
    configCollocamento: ConfigCollocamento;
    abilitaSchedulazioneTravaso: boolean;
    insertTrace: Trace;
    lastUpdateTrace: Trace;
    motivoChiusura: number,
    chiusuraAziendale1: PeriodoGiustificato;
    chiusuraAziendale2: PeriodoGiustificato;
    bgColor:string,
}

export class AziendaRecord {

    static isMaster = (azienda: AziendaRecord) => azienda && azienda.idAzienda === azienda.idAziendaMaster;
    static isINPS = (azienda: AziendaRecord) => azienda && azienda.idAzienda === azienda.idAziendaInps;
    static isINAIL = (azienda: AziendaRecord) => azienda && azienda.idAzienda === azienda.idAziendaInail;
    static isUL = (azienda: AziendaRecord) => azienda && (azienda.idAzienda !== azienda.idAziendaMaster) || azienda.consideraComeUL;

    static isActive = (azienda: AziendaRecord) => {
        const ora = DateTime.now().startOf('day');
        const validitaInizio = azienda.validitaInizio !== null ? DateTime.fromISO(azienda.validitaInizio) : ora;
        const validitaFine = azienda.validitaFine !== null ? DateTime.fromISO(azienda.validitaFine) : ora;
        return (validitaInizio <= ora && validitaFine >= ora);
    };
    static hasEnded = (azienda: AziendaRecord) => {
        const ora = DateTime.now().startOf('day');
        const validitaFine = azienda.validitaFine !== null ? DateTime.fromISO(azienda.validitaFine) : ora;
        return validitaFine < ora;
    };
    static isStarting = (azienda: AziendaRecord) => {
        const ora = DateTime.now().startOf('day');
        const validitaInizio = azienda.validitaInizio !== null ? DateTime.fromISO(azienda.validitaInizio) : ora;
        return validitaInizio > ora;
    };

    static getDescrizione = (elem: AziendaRecord) => {
        return elem ? elem.descrizione : '???';
    }
}
